import type { GridApi, MenuItemDef } from 'ag-grid-enterprise';
import { t, type I18nLocale } from '@ardoq/i18n';
import { columnOperations } from './columnOperations';
import { autoSizeAllColumnsWhenRowsSettle } from './autoSizeColumns';

type ResetColumnsMenuItemArgs<TData> = {
  api: GridApi<TData>;
  i18nLocale: I18nLocale;
  defaultVisibleColumnKeys: readonly string[];
  getFieldOrderByColumnKey: () => ReadonlyMap<string, number>;
};

/**
 * AG Grid's default "Reset columns" restores the initial column definitions. As we save
 * and restore the column selection state, that would restore the saved state instead of
 * the defaults. Instead, we mark all columns as visible only if they are default, unpin
 * them, and restore the default order the grid is built with.
 */
export const getResetColumnsMenuItem = <TData>({
  api,
  i18nLocale,
  defaultVisibleColumnKeys,
  getFieldOrderByColumnKey,
}: ResetColumnsMenuItemArgs<TData>): MenuItemDef<TData> => ({
  name: t(
    i18nLocale,
    'inventoryApp:ag.columnMenu.resetColumns',
    'Reset Columns'
  ),
  action: () => {
    const columnDefs = columnOperations.toLeafColumnDefs<TData>(
      api.getColumnDefs() ?? []
    );
    api.applyColumnState({
      state: columnOperations.getDefaultColumnDefinitions(
        columnDefs,
        defaultVisibleColumnKeys,
        getFieldOrderByColumnKey()
      ),
      applyOrder: true,
    });
    autoSizeAllColumnsWhenRowsSettle(api);
  },
});
